import React from 'react';
import { _, am5, am5radar, am5themes_Animated } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { FONT_FAMILY, FONT_SIZE } from '../../../theme/layout-size';
import { createDownloadButton } from '../shared/create-download-button';

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  labels: types.widgets.TableData;
  dimensions: string[];
  fullScreen: boolean;
  download: boolean;
};

const Chart = ({ id, data, labels, dimensions, fullScreen, download }: ChartProps) => {
  const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;

  React.useEffect(() => {
    // Create root element
    const root = am5.Root.new(widgetId);

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Remove amCharts logo
    root._logo?.dispose();

    const chart = root.container.children.push(
      am5radar.RadarChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
      })
    );

    const cursor = chart.set(
      'cursor',
      am5radar.RadarCursor.new(root, {
        behavior: 'zoomX',
      })
    );

    cursor.lineY.set('visible', false);
    cursor.lineX.set('visible', false);

    const maxValue = _.max((data || []).map((el) => _.max(Object.values(_.omit(el, 'spoke'))))) as number;

    const { xAxis, yAxis } = createAxes({
      root,
      chart,
      maxValue,
    });

    createSeries({
      root,
      chart,
      data,
      labels,
      dimensions,
      xAxis,
      yAxis,
    });

    const legend = chart.children.push(
      am5.Legend.new(root, {
        x: am5.percent(50),
        centerX: am5.percent(35),
      })
    );

    legend.labels.template.setAll({
      fontSize: FONT_SIZE.xxs,
      fontFamily: FONT_FAMILY,
    });

    legend.data.setAll(chart.series.values);
    if (download) createDownloadButton(root, 'spoke', data);

    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [data]);

  return <div id={widgetId} style={{ width: '100%', height: '100%' }} />;
};

export default Chart;
